import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { SignOut, UserCircle, Crown, CheckCircle } from "@phosphor-icons/react";
import UpgradeButton from "@/components/UpgradeButton";

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/profile", { replace: true });
  }, [loading, user, navigate]);

  if (loading) return <PageShell title="Loading…" />;
  if (!user) return null;

  const isGuest = user.role === "guest";
  const fields = [
    ["Name", user.name],
    ...(user.email ? [["Email", user.email]] : []),
    ["Role", isGuest ? "Team guest" : user.role],
    ["Plan", user.plan],
    ["Joined", new Date(user.created_at).toLocaleString()],
    ["User ID", user.user_id],
  ];

  return (
    <PageShell
      eyebrow="Profile"
      title="Your account."
      subtitle="Profile and plan. Need to delete your account? Email privacy@sendme.co.in."
      narrow
    >
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
        <div className="brut-card p-6 text-center">
          <div className="w-20 h-20 mx-auto bg-black text-white grid place-items-center text-3xl font-black rounded-full">
            {(user.name || "?")[0].toUpperCase()}
          </div>
          <div className="mt-4 text-xl font-bold">{user.name}</div>
          {user.email && <div className="text-sm font-mono text-black/55">{user.email}</div>}

          {user.role === "admin" && (
            <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest bg-[#FF3366] text-white px-2 py-1">
              <Crown size={12} weight="fill"/> Admin
            </div>
          )}
          {isGuest && (
            <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest bg-black/70 text-white px-2 py-1">
              Team guest
            </div>
          )}
          {user.plan === "premium" && (
            <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest bg-[#10B981] text-white px-2 py-1">
              <CheckCircle size={12} weight="fill"/> Premium
            </div>
          )}
          {!isGuest && user.plan !== "premium" && (
            <div className="mt-4"><UpgradeButton/></div>
          )}

          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="btn-brut w-full mt-6" data-testid="logout"
          >
            <SignOut size={16}/> Sign out
          </button>
        </div>

        <div className="space-y-6">
          <div className="brut-card p-6" data-testid="profile-fields">
            <div className="text-sm mb-4 flex items-center gap-2"><UserCircle size={14}/> Account details</div>
            <dl className="divide-y divide-black/10">
              {fields.map(([k, v]) => (
                <div key={k} className="py-3 flex items-start justify-between gap-3">
                  <dt className="text-sm text-black/55">{k}</dt>
                  <dd className="font-mono text-sm text-right break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
